import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react'
import './App.scss'
import AppRouter from './components/AppRouter'
// import { Search } from './components/Search'
import { authStore } from './store/auth'
import Cookies from 'universal-cookie'
import { ConfigProvider, notification } from 'antd'
import { css } from '@emotion/css'
import { RNLogin, RNLogout, RNWelcome } from './helpers/routeNames'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppHeader } from './components/AppHeader'
import { NavBar } from './components/NavBar'
import { IDBPDatabase, openDB } from 'idb'
import { Update } from './helpers/types/update'
import { updateStore } from './store/update'
import { initSocket } from './socket'
import { relayStore } from './store/relay'
import { notificationStore } from './store/notification'
import { NotificationType } from './helpers/types/notification'
import { resetStaleCache } from './helpers/cacheVersion'

function App() {
	const cookie = useMemo(() => new Cookies(), [])
	const navigate = useNavigate()
	const location = useLocation()
	const { isLoggedIn, logout, refresh, userinfo, user } =
		authStore()
	const { fetchUpdates } = updateStore()
	const { setDataFromUpdate, clearAll } = relayStore()
	const { prependNotification } = notificationStore()
	const [isSyncLoading, setIsSyncLoading] = useState(false)

	const [, contextHolder] = notification.useNotification({
		stack: { threshold: 3 }
	})

	useEffect(() => {
		const init = async () => {
			if (!cookie.get('access')) {
				if (!cookie.get('refresh')) {
					if (
						location.pathname !== RNLogin &&
						location.pathname !== RNLogout
					) {
						// NEEDED TO FIX THIS WITH HTTP ONLY COOKIES MODE
						navigate(
							'/login?path=' +
								encodeURIComponent(
									location.pathname +
										location.search +
										location.hash
								)
						)
						logout()
						clearAll()
					}
				} else {
					await refresh()
					navigate(
						location.pathname === RNLogin
							? RNWelcome
							: location.pathname +
									location.search +
									location.hash
					)
				}
			} else {
				try {
					await userinfo()
				} catch (e) {
					if (!!cookie.get('refresh')) {
						logout()
						clearAll()
					}
				}
			}
		}
		init()
	}, [
		cookie,
		navigate,
		location.pathname,
		location.hash,
		location.search,
		refresh,
		userinfo,
		logout,
		clearAll
	])

	const initDB = async () => {
		const db = await openDB('relayDB', 1, {
			upgrade(db) {
				if (!db.objectStoreNames.contains('electric')) {
					db.createObjectStore('electric', {
						keyPath: 'id'
					})
				}
				if (!db.objectStoreNames.contains('mechanic')) {
					db.createObjectStore('mechanic', {
						keyPath: 'id'
					})
				}
				if (!db.objectStoreNames.contains('relay')) {
					db.createObjectStore('relay', { keyPath: 'id' })
				}
				if (!db.objectStoreNames.contains('relayType')) {
					db.createObjectStore('relayType', {
						keyPath: 'id'
					})
				}
			}
		})
		return db
	}

	const setToStoreFromIndexDB = useCallback(async () => {
		if (!isLoggedIn) return
		const db = await openDB('relayDB', 1)
		const tx = db.transaction(
			['electric', 'mechanic', 'relay', 'relayType'],
			'readwrite'
		)
		const electricStore = tx.objectStore('electric')
		const mechanicStore = tx.objectStore('mechanic')
		const relayStore = tx.objectStore('relay')
		const relayTypeStore = tx.objectStore('relayType')

		const electricData = await electricStore.getAll()
		const mechanicData = await mechanicStore.getAll()
		const relayData = await relayStore.getAll()
		const relayTypeData = await relayTypeStore.getAll()

		relayTypeData.forEach((data) => {
			setDataFromUpdate({
				action: 'create',
				data,
				type: 'relayType'
			})
		})

		electricData.forEach((data) => {
			setDataFromUpdate({
				action: 'create',
				data,
				type: 'electric'
			})
		})
		mechanicData.forEach((data) => {
			setDataFromUpdate({
				action: 'create',
				data,
				type: 'mechanic'
			})
		})
		relayData.forEach((data) => {
			setDataFromUpdate({
				action: 'create',
				data,
				type: 'relay'
			})
		})
	}, [setDataFromUpdate, isLoggedIn])

	const applyUpdates = useCallback(
		async (db: IDBPDatabase, updates: Update[]) => {
			if (updates.length === 0) return
			const tx = db.transaction(
				['electric', 'mechanic', 'relay', 'relayType'],
				'readwrite'
			)
			for (const update of updates) {
				const store = tx.objectStore(update.type)
				const data = JSON.parse(update.data)

				setDataFromUpdate({
					action: update.operation,
					data,
					type: update.type
				})

				if (
					update.operation === 'create' ||
					update.operation === 'update'
				) {
					store.put(data)
				} else if (update.operation === 'delete') {
					store.delete(data.id)
				}
			}

			await tx.done

			localStorage.setItem(
				'lastUpdateId',
				updates[updates.length - 1].id.toString()
			)
		},
		[setDataFromUpdate]
	)

	const syncData = useCallback(async () => {
		if (!isLoggedIn) return
		setIsSyncLoading(true)
		const db = await initDB()
		const lastUpdateId = localStorage.getItem('lastUpdateId') || 0
		const updates = await fetchUpdates(+lastUpdateId)
		await applyUpdates(db, updates)
		if (updates.length > 0) {
			const newLastUpdateId = updates[updates.length - 1].id
			localStorage.setItem(
				'lastUpdateId',
				newLastUpdateId.toString()
			)
		}
		setIsSyncLoading(false)
	}, [fetchUpdates, applyUpdates, isLoggedIn])

	useEffect(() => {
		if (!isLoggedIn) return

		const socket = initSocket(user?.id)
		socket.on('update', async (update: Update) => {
			const db = await initDB()
			await applyUpdates(db, [update])
		})
		socket.on(
			'notification',
			(payload: {
				message: string
				recordId: string
				relayTypeId?: string
				rejectionReason?: string
			}) => {
				const type: NotificationType = payload.rejectionReason
					? 'REJECTED'
					: payload.message.includes('утверждена')
					? 'APPROVED'
					: 'STATUS_CHANGED'
				prependNotification({
					id: `socket-${Date.now()}-${Math.random()}`,
					userId: user?.id ?? '',
					type,
					message: payload.message,
					relatedId: payload.recordId ?? null,
					isRead: false,
					createdAt: new Date().toISOString()
				})
			}
		)
		return () => {
			socket.disconnect()
		}
	}, [isLoggedIn, cookie, applyUpdates, prependNotification, user?.id])

	// Must finish before anything opens relayDB: a device that synced before the
	// header-hierarchy migration still holds the old rows, and would never
	// re-fetch them because its lastUpdateId is already past them.
	useEffect(() => {
		let cancelled = false
		resetStaleCache().finally(() => {
			if (cancelled) return
			syncData()
			setToStoreFromIndexDB()
		})
		return () => {
			cancelled = true
		}
	}, [syncData, setToStoreFromIndexDB, isLoggedIn, cookie])

	const { getPrefixCls } = useContext(ConfigProvider.ConfigContext)
	const rootPrefixCls = getPrefixCls()
	const linearGradientButton = css`
		&.${rootPrefixCls}-btn-primary:not([disabled]):not(
				.${rootPrefixCls}-btn-dangerous
			) {
			border-width: 0;

			> span {
				position: relative;
			}

			&::before {
				content: '';
				background: linear-gradient(135deg, #6253e1, #04befe);
				position: absolute;
				inset: 0;
				opacity: 1;
				transition: all 0.3s;
				border-radius: inherit;
			}

			&:hover::before {
				opacity: 0;
			}
		}
	`

	return (
		<ConfigProvider
			direction="ltr"
			theme={{ token: { zIndexPopupBase: 1200 } }}
			locale={{
				locale: 'ru',
				Form: {
					defaultValidateMessages: {
						required: () =>
							'Поле обязательно для заполнения'
					}
				}
			}}
			button={{
				className: linearGradientButton
			}}>
			{isSyncLoading && (
				<div className="loading-icon">Loading...</div>
			)}

			{(!!isLoggedIn || !!cookie.get('access')) && user && (
				<>
					{contextHolder}
					<AppHeader />
					<section className="app-layout-content">
						<AppRouter
							isLoggedIn={
								!!isLoggedIn || !!cookie.get('access')
							}
							user={user}
						/>
					</section>
					<NavBar role={user.role} />
				</>
			)}
			{!isLoggedIn && !cookie.get('access') && (
				<AppRouter isLoggedIn={isLoggedIn} user={false} />
			)}
		</ConfigProvider>
	)
}

export default App
